import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { miniApp } from '@telegram-apps/sdk-react';
import * as Sentry from '@sentry/react';
import { Layout } from './components/Layout';
import { AppGate } from './components/AppGate';
import { ComingSoon } from './components/ComingSoon';
import { SessionExpiredModal } from './components/SessionExpiredModal';
import { WizardProvider } from './state/wizard';
import { onSessionExpired } from './lib/api';
import { HomePage } from './pages/HomePage';
import { SceneStep } from './pages/wizard/SceneStep';
import { KissTypeStep } from './pages/wizard/KissTypeStep';
import { DurationStep } from './pages/wizard/DurationStep';
import { GeneratingPage } from './pages/GeneratingPage';
import { ResultPage } from './pages/ResultPage';
import { LibraryPage } from './pages/LibraryPage';
import { ProfilePage } from './pages/ProfilePage';

export default function App() {
  // Production "Coming Soon" mode — full app is hidden until launch.
  // Toggled via VITE_COMING_SOON env var at build time (GH Actions workflow).
  if (import.meta.env.VITE_COMING_SOON === 'true') {
    return <ComingSoon />;
  }

  return (
    <AppGate>
      <WizardProvider>
        <BrowserRouter>
          <Layout>
            {/*
             * Sentry ErrorBoundary catches render-time exceptions in any
             * route, reports them, and shows a recovery wall. Wrapping
             * only <Routes> (not the whole Layout) means BottomNav +
             * SessionExpiredGate stay alive — user can still navigate
             * away from the broken page without reload.
             */}
            <Sentry.ErrorBoundary fallback={<RouteCrashFallback />} showDialog={false}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/wizard/scene" element={<SceneStep />} />
                <Route path="/wizard/type" element={<KissTypeStep />} />
                <Route path="/wizard/duration" element={<DurationStep />} />
                <Route path="/generating" element={<GeneratingPage />} />
                <Route path="/result" element={<ResultPage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Routes>
            </Sentry.ErrorBoundary>
            {/*
             * SessionExpiredGate must live INSIDE Layout (which wraps
             * everything in <AppRoot>) because SessionExpiredModal uses
             * @telegram-apps/telegram-ui Button + Text components that
             * crash silently without AppRoot context — the result was a
             * blank white screen instead of the modal.
             *
             * Outside the ErrorBoundary on purpose: if a route crashes,
             * the modal still needs to be able to show + recover the
             * session, which is the user's primary out-of-error path.
             */}
            <SessionExpiredGate />
          </Layout>
        </BrowserRouter>
      </WizardProvider>
    </AppGate>
  );
}

/**
 * Listens to onSessionExpired() events from the API layer and renders the
 * blocking modal when the Worker rejects with `401 expired`. Mounted at App
 * root so a single instance covers all routes.
 *
 * Telegram-issued initData lifetime is fixed per Mini App launch; once it
 * expires the only recovery is closing + reopening the app. We call
 * `miniApp.close()` on the user's tap which fully shuts the Mini App down
 * — they then tap the bot's menu button again to reopen with fresh initData.
 */
function SessionExpiredGate() {
  const [detail, setDetail] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(1);

  useEffect(() => {
    return onSessionExpired((d, a) => {
      // Lock the modal once it's open — subsequent expired events from
      // pending requests shouldn't reset the message. attempt updates
      // freely so a stronger copy can override a weaker one if a later
      // event has higher count (rare in practice).
      setDetail((prev) => prev ?? d);
      setAttempt((prev) => Math.max(prev, a));
    });
  }, []);

  if (!detail) return null;

  return (
    <SessionExpiredModal
      detail={detail}
      attempt={attempt}
      onClose={() => {
        try {
          // Close Mini App. After this fires the user is back in the bot
          // chat; tapping menu button reopens with fresh initData.
          if (miniApp.close.isAvailable()) {
            miniApp.close();
            return;
          }
        } catch (e) {
          console.warn('[session-expired] miniApp.close failed', e);
        }
        // Fallback for environments where close() isn't available
        // (Chrome dev preview, very old Telegram clients): reload the
        // page — at least clears React state. User still needs to
        // reopen properly via Telegram for fresh initData.
        window.location.reload();
      }}
    />
  );
}

/**
 * Shown when a route component throws during render. Sentry has already
 * captured the error by this point (the boundary wraps reporting → UI).
 * Offers a single recovery action: reload the page, which re-runs auth
 * + brings the user back to /. We don't try a soft React state reset
 * because the crash may have left context providers in a bad state.
 */
function RouteCrashFallback() {
  return (
    <div
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <div style={{ fontSize: 48 }}>💔</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--tgui--text_color)' }}>
        Something went wrong
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--tgui--hint_color)',
          maxWidth: 280,
          lineHeight: 1.5,
        }}
      >
        We've been notified. Try reloading — your progress in the wizard might be lost.
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: 8,
          border: 'none',
          background: 'linear-gradient(135deg, #FF6B9D 0%, #C44569 100%)',
          color: '#fff',
          padding: '10px 22px',
          borderRadius: 12,
          fontWeight: 600,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  );
}
